import { useCallback, useState } from "react";

import useOnDepsChange from "./use-on-deps-change.hook";

export type DetailsMode = "view" | "edit";
export type EditBottomTab = "sessions" | "context";

type ScribeDetailsModeParams = {
  scribeId: number | undefined;
  /** Set by a parent that owns the mode; unset, the details keep it themselves. */
  detailsMode?: DetailsMode;
  onDetailsModeChange?: (mode: DetailsMode) => void;
  editBottomTab?: EditBottomTab;
  onEditBottomTabChange?: (tab: EditBottomTab) => void;
  /** Atomically sets the edit sub-tab and the mode (used when the parent owns both). */
  onEnterEdit?: (bottomTab: EditBottomTab) => void;
  onEditFinalized?: () => void;
};

/**
 * View Scribe vs Audio Recordings, controlled by the parent or kept here.
 *
 * Both parents keep the bottom tab on "sessions" and reset it themselves when the recording
 * changes, so nothing here pushes it back to them.
 */
export default function useScribeDetailsMode({
  scribeId,
  detailsMode,
  onDetailsModeChange,
  editBottomTab: editBottomTabProp,
  onEditBottomTabChange,
  onEnterEdit,
  onEditFinalized
}: ScribeDetailsModeParams) {
  const isControlled = detailsMode !== undefined;
  const [internalMode, setInternalMode] = useState<DetailsMode>("view");
  const mode = detailsMode ?? internalMode;
  // Another recording opens on its view, as it would in a fresh dialog.
  useOnDepsChange([scribeId], () => {
    if (!isControlled) setInternalMode("view");
  });

  const setMode = useCallback(
    (next: DetailsMode) => {
      if (isControlled) onDetailsModeChange?.(next);
      else setInternalMode(next);
    },
    [isControlled, onDetailsModeChange]
  );

  const isEditBottomTabControlled = editBottomTabProp !== undefined;
  const [internalEditBottomTab, setInternalEditBottomTab] =
    useState<EditBottomTab>("sessions");
  const editBottomTab = editBottomTabProp ?? internalEditBottomTab;

  const setEditBottomTab = useCallback(
    (tab: EditBottomTab) => {
      if (isEditBottomTabControlled) onEditBottomTabChange?.(tab);
      else setInternalEditBottomTab(tab);
    },
    [isEditBottomTabControlled, onEditBottomTabChange]
  );

  const handleEditFinalized = useCallback(() => {
    setEditBottomTab("sessions");
    onEditFinalized?.();
    setMode("view");
  }, [onEditFinalized, setMode, setEditBottomTab]);

  const handleModeTabChange = useCallback(
    (_: unknown, next: DetailsMode) => {
      if (next === mode) return;
      if (next === "edit") {
        if (onEnterEdit) {
          onEnterEdit("sessions");
          return;
        }
        setEditBottomTab("sessions");
      }
      setMode(next);
    },
    [mode, setMode, setEditBottomTab, onEnterEdit]
  );

  return { mode, editBottomTab, handleEditFinalized, handleModeTabChange };
}
